import { useEffect, useState, useRef } from 'react';
import { Trophy, Star, Sparkles, Crown } from 'lucide-react';

import { CDN_URL } from '@/config/cdn';

// Mastery level configurations
const LEVEL_CONFIG = {
  shokyu: {
    label: 'Shokyu',
    kanji: '初級',
    english: 'Beginner',
    color: 'from-green-500 to-green-600',
    bgColor: 'bg-green-500',
    textColor: 'text-green-600',
    icon: Star,
    sound: 'soft-chime.mp3',
    animation: 'animate-fade-in',
  },
  chukyu: {
    label: 'Chukyu',
    kanji: '中級',
    english: 'Intermediate',
    color: 'from-blue-500 to-blue-600',
    bgColor: 'bg-blue-500',
    textColor: 'text-blue-600',
    icon: Trophy,
    sound: 'level-up.mp3',
    animation: 'animate-slide-up',
  },
  jokyu: {
    label: 'Jokyu',
    kanji: '上級',
    english: 'Advanced',
    color: 'from-purple-500 to-purple-600',
    bgColor: 'bg-purple-500',
    textColor: 'text-purple-600',
    icon: Sparkles,
    sound: 'achievement.mp3',
    animation: 'animate-pulse-glow',
  },
  tatsujin: {
    label: 'Tatsujin',
    kanji: '達人',
    english: 'Master',
    color: 'from-yellow-400 to-amber-500',
    bgColor: 'bg-yellow-500',
    textColor: 'text-yellow-600',
    icon: Crown,
    sound: 'gong.mp3',
    animation: 'animate-shimmer',
  },
};

/**
 * MasteryLevelUpNotification - Displays when a word reaches a new mastery level
 *
 * Props:
 * - level: string - The new mastery level (shokyu, chukyu, jokyu, tatsujin)
 * - termJapanese: string - The Japanese word
 * - termRomaji: string - The romaji reading
 * - termEnglish: string - The English translation
 * - bonusPoints: number - Points awarded for reaching this level
 * - onComplete: function - Called when notification should be dismissed
 * - autoDismiss: number - Auto-dismiss delay in ms (default 3000)
 */
export function MasteryLevelUpNotification({
  level,
  termJapanese,
  termRomaji,
  termEnglish,
  bonusPoints,
  onComplete,
  autoDismiss = 3000,
}) {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const audioRef = useRef(null);

  const config = LEVEL_CONFIG[level] || LEVEL_CONFIG.shokyu;
  const IconComponent = config.icon;

  useEffect(() => {
    // Play sound effect
    if (audioRef.current && config.sound) {
      // Try CloudFront first, fallback to nothing
      audioRef.current.src = `${CDN_URL}/audio/${config.sound}`;
      audioRef.current.volume = 0.5;
      audioRef.current.play().catch(() => {
        // Sound file may not exist yet, that's ok
        console.log(`Sound file ${config.sound} not available`);
      });
    }

    // Auto-dismiss after delay
    const timer = setTimeout(() => {
      handleDismiss();
    }, autoDismiss);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center pointer-events-none
        ${isExiting ? 'animate-fade-out' : 'animate-fade-in'}`}
      onClick={handleDismiss}
    >
      <audio ref={audioRef} />

      <div
        className={`pointer-events-auto max-w-sm mx-4 rounded-2xl overflow-hidden shadow-2xl
          transform transition-all duration-300
          ${isExiting ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}
          bg-gradient-to-br ${config.color}`}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4
            animate-bounce">
            <IconComponent className="h-8 w-8 text-white" />
          </div>

          <h2 className="text-xl font-bold text-white mb-1">
            Level Up!
          </h2>
          <p className="text-white/80 text-sm">
            {config.label} ({config.kanji}) • {config.english}
          </p>
        </div>

        {/* Term */}
        <div className="bg-white/10 px-6 py-4">
          <p className="text-2xl font-bold text-white text-center mb-1">
            {termJapanese}
          </p>
          {termRomaji && (
            <p className="text-white/80 text-center text-sm">
              {termRomaji}
            </p>
          )}
          {termEnglish && (
            <p className="text-white/60 text-center text-sm mt-1">
              {termEnglish}
            </p>
          )}
        </div>

        {/* Points */}
        {bonusPoints > 0 && (
          <div className="px-6 py-3 text-center bg-white/5">
            <span className="text-white font-medium">
              +{bonusPoints} bonus points!
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * CategoryCompletionNotification - Displays when all words in a category reach a mastery level
 *
 * Props:
 * - level: string - The mastery level achieved
 * - category: string - The category name
 * - difficulty: number - The difficulty level
 * - bonusPoints: number - Points awarded
 * - newDifficultyUnlocked: number|null - If new difficulty was unlocked
 * - onComplete: function - Called when notification should be dismissed
 */
export function CategoryCompletionNotification({
  level,
  category,
  difficulty,
  bonusPoints,
  newDifficultyUnlocked,
  onComplete,
  autoDismiss = 4000,
}) {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const audioRef = useRef(null);

  const config = LEVEL_CONFIG[level] || LEVEL_CONFIG.shokyu;

  useEffect(() => {
    // Play celebration sound
    if (audioRef.current) {
      audioRef.current.src = `${CDN_URL}/audio/gong.mp3`;
      audioRef.current.volume = 0.6;
      audioRef.current.play().catch(() => {});
    }

    const timer = setTimeout(() => {
      handleDismiss();
    }, autoDismiss);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 pointer-events-auto
        ${isExiting ? 'animate-fade-out' : 'animate-fade-in'}`}
      onClick={handleDismiss}
    >
      <audio ref={audioRef} />

      <div
        className={`max-w-md mx-4 rounded-3xl overflow-hidden shadow-2xl
          transform transition-all duration-300
          ${isExiting ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}
          bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400`}
      >
        {/* Confetti background effect */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                backgroundColor: ['#FFD700', '#FF69B4', '#00CED1', '#98FB98'][i % 4],
                animationDelay: `${Math.random() * 2}s`,
              }}
            />
          ))}
        </div>

        <div className="relative px-8 py-8 text-center">
          {/* Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 mb-4
            animate-pulse">
            <Trophy className="h-10 w-10 text-white" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white mb-2">
            Category Complete!
          </h2>

          {/* Details */}
          <p className="text-white/90 text-lg mb-4">
            All <span className="font-bold">{category}</span> Level {difficulty} words
            <br />
            reached <span className={`font-bold ${config.textColor}`}>{config.label}</span>!
          </p>

          {/* Points */}
          {bonusPoints > 0 && (
            <div className="bg-white/20 rounded-xl py-3 px-6 mb-4 inline-block">
              <span className="text-white text-xl font-bold">
                +{bonusPoints} points!
              </span>
            </div>
          )}

          {/* New difficulty unlocked */}
          {newDifficultyUnlocked && (
            <div className="mt-4 flex items-center justify-center gap-2 text-white animate-bounce">
              <Sparkles className="h-5 w-5" />
              <span className="font-medium">
                Level {newDifficultyUnlocked} words unlocked!
              </span>
              <Sparkles className="h-5 w-5" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Add custom animations via style tag
const style = document.createElement('style');
style.textContent = `
  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes fade-out {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes slide-up {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  @keyframes confetti {
    0% { transform: translateY(0) rotate(0deg); opacity: 1; }
    100% { transform: translateY(100px) rotate(360deg); opacity: 0; }
  }
  .animate-fade-in { animation: fade-in 0.3s ease-out; }
  .animate-fade-out { animation: fade-out 0.3s ease-out; }
  .animate-slide-up { animation: slide-up 0.4s ease-out; }
  .animate-confetti { animation: confetti 3s ease-out infinite; }
`;
if (!document.getElementById('mastery-notification-styles')) {
  style.id = 'mastery-notification-styles';
  document.head.appendChild(style);
}

export default MasteryLevelUpNotification;
